import { useEffect, useState, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, ArrowRight, CheckCircle2, Search, Award, Compass, Handshake, Clock, ChevronDown, ChevronRight, Check, X, Sparkles } from "lucide-react";
import { StatCard, EmptyState, ProgressRing } from "@/components/crm/UiBits";
import { TOTAL_LESSON_COUNT } from "@/lib/tracks";
import { formatDisplayName } from "@/lib/names";

const STAGE_LABEL = {
  new: "New",
  monday_night: "Introduction",
  church_study: "Church Study",
  ready_for_assignment: "Just assigned",
  home_study: "Home Study",
  first_steps: "Next Steps",
  completed: "Completed",
};

function GuestCard({ g, pendingRequest, onRequest, onCancelRequest }) {
  const done = { m: g.progress?.monday?.done || 0, c: g.progress?.church?.done || 0, h: g.progress?.home?.done || 0, fs: g.progress?.first_steps?.done || 0 };
  const total = { m: g.progress?.monday?.total || 1, c: g.progress?.church?.total || 3, h: g.progress?.home?.total || 7, fs: g.progress?.first_steps?.total || 24 };
  const overallDone = (g.progress?.initial_contact?.done || 0) + done.m + done.c + done.h + done.fs;
  const anyMine = g.is_mine || g.is_mine_next_steps;
  const isRequested = !!pendingRequest;
  const unassigned = !anyMine && (g.assigned_teachers?.length || 0) === 0;
  const showRequest = unassigned && !isRequested;
  const clickRequest = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRequest(g);
  };
  const clickCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCancelRequest(pendingRequest);
  };
  return (
    <Link
      to={`/teacher/guests/${g.id}`}
      data-testid={`teacher-guest-card-${g.id}`}
      className="card-surface px-3 py-2.5 block"
      style={{ opacity: anyMine ? 1 : 0.92 }}
    >
      <div className="flex items-center gap-3">
        <ProgressRing size={34} stroke={3} value={overallDone} total={TOTAL_LESSON_COUNT} color={anyMine ? "var(--ember)" : "var(--muted)"} label={`${overallDone} of ${TOTAL_LESSON_COUNT} sessions complete`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
            <h3 className="font-serif-display text-base leading-tight truncate" style={{ color: "var(--ink)" }}>
              {formatDisplayName(g)}
            </h3>
            {g.is_mine && (
              <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "var(--ember)", color: "var(--bg-base)" }} data-testid={`teacher-mine-badge-${g.id}`}>Phase 1</span>
            )}
            {g.is_mine_next_steps && (
              <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "var(--gold)", color: "var(--bg-base)" }} data-testid={`teacher-mine-next-badge-${g.id}`}>Next Steps</span>
            )}
            {unassigned && !isRequested && (
              <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "var(--surface-hover)", color: "var(--gold)", border: "1px solid var(--gold)" }} data-testid={`teacher-unassigned-${g.id}`}>
                Unassigned
              </span>
            )}
            {isRequested && (
              <button
                onClick={clickCancel}
                className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 transition-colors hover:opacity-80"
                style={{ background: "var(--surface-hover)", color: "var(--muted)", border: "1px solid var(--border-subtle)" }}
                data-testid={`teacher-requested-${g.id}`}
                title="Click to withdraw this request"
              >
                <Clock size={9} /> Requested · <span style={{ color: "var(--terracotta)" }}>Cancel</span>
              </button>
            )}
            <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: g.stage === "completed" ? "var(--olive)" : "var(--surface-hover)", color: g.stage === "completed" ? "var(--bg-base)" : "var(--ink-2)" }}>
              {STAGE_LABEL[g.stage] || g.stage}
            </span>
            {showRequest && (
              <button
                onClick={clickRequest}
                className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.12em] rounded-full transition-colors"
                style={{ background: "var(--gold)", color: "var(--bg-base)", fontWeight: 600 }}
                data-testid={`teacher-request-guest-${g.id}`}
              >
                <Handshake size={10} /> Request
              </button>
            )}
          </div>
          <div className="mt-0.5 text-[0.7rem] flex flex-wrap gap-x-3 gap-y-0" style={{ color: "var(--muted)" }}>
            {g.phone && <span>{g.phone}</span>}
            {g.city && <span>{g.city}</span>}
            {g.is_mine === false && (g.assigned_teachers?.length || 0) > 0 && (
              <span data-testid={`teacher-guest-teachers-${g.id}`}>
                with {g.assigned_teachers.map((t) => t.name).join(" & ")}
              </span>
            )}
          </div>
          <div className="mt-1.5 grid grid-cols-4 gap-x-3 gap-y-0 text-[0.7rem]">
            {[["Intro", done.m, total.m, "var(--terracotta)"], ["Church", done.c, total.c, "#60A5FA"], ["Home", done.h, total.h, "var(--olive)"], ["Next", done.fs, total.fs, "var(--gold)"]].map(([label, d, t, color], i) => (
              <div key={i} className="flex items-center gap-1.5 min-w-0">
                <span className="text-[0.6rem] uppercase tracking-wider shrink-0" style={{ color: "var(--muted)" }}>{label}</span>
                <span className="shrink-0" style={{ color: "var(--ink)" }}>{d}/{t}</span>
                <div className="flex-1 h-[3px] rounded-full overflow-hidden" style={{ background: "var(--bg-base)" }}>
                  <div className="h-full rounded-full" style={{ width: `${(d / t) * 100}%`, background: color, transition: "width 0.3s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <ArrowRight size={14} style={{ color: "var(--muted)" }} className="shrink-0" />
      </div>
    </Link>
  );
}

function PastRequestsPanel({ requests }) {
  const [open, setOpen] = useState(false);
  const decided = useMemo(
    () => (requests || [])
      .filter((r) => r.status === "approved" || r.status === "denied")
      .sort((a, b) => (b.decided_at || "").localeCompare(a.decided_at || ""))
      .slice(0, 20),
    [requests],
  );
  if (decided.length === 0) return null;
  const approvedCount = decided.filter((r) => r.status === "approved").length;
  const deniedCount = decided.length - approvedCount;
  return (
    <div className="mb-6 rounded-md" style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }} data-testid="teacher-past-requests-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        data-testid="teacher-past-requests-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} style={{ color: "var(--muted)" }} /> : <ChevronRight size={14} style={{ color: "var(--muted)" }} />}
          <span className="text-xs uppercase tracking-[0.16em]" style={{ color: "var(--ink-2)" }}>
            My past requests
          </span>
          <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>
            {approvedCount} approved · {deniedCount} denied
          </span>
        </div>
        <span className="text-[0.6rem]" style={{ color: "var(--muted)" }}>
          Last {decided.length}
        </span>
      </button>
      {open && (
        <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }} data-testid="teacher-past-requests-list">
          {decided.map((r) => {
            const approved = r.status === "approved";
            const when = r.decided_at ? new Date(r.decided_at).toLocaleDateString() : "";
            return (
              <li key={r.id} className="px-4 py-2.5 flex items-start gap-3" data-testid={`teacher-past-request-${r.id}`}>
                <span
                  className="inline-flex items-center justify-center rounded-full shrink-0"
                  style={{
                    width: 22, height: 22,
                    background: approved ? "var(--olive)" : "var(--surface-hover)",
                    color: approved ? "var(--bg-base)" : "var(--terracotta)",
                    border: approved ? "none" : "1px solid var(--terracotta)",
                  }}
                >
                  {approved ? <Check size={12} /> : <X size={12} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm" style={{ color: "var(--ink)" }}>
                      {r.guest_name || `Guest ${(r.guest_id || "").slice(0, 6)}`}
                    </span>
                    <span
                      className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                      style={{
                        background: approved ? "var(--olive)" : "var(--surface-hover)",
                        color: approved ? "var(--bg-base)" : "var(--terracotta)",
                        border: approved ? "none" : "1px solid var(--terracotta)",
                      }}
                    >
                      {approved ? "Approved" : "Denied"}
                    </span>
                    {when && (
                      <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>{when}</span>
                    )}
                  </div>
                  {r.decision_note && (
                    <div className="mt-0.5 text-xs" style={{ color: "var(--muted)" }} data-testid={`teacher-past-request-note-${r.id}`}>
                      "{r.decision_note}"
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function TeacherDashboard() {
  const { user, loading } = useAuth();
  const [filter, setFilter] = useState("active");
  const [scope, setScope] = useState("mine"); // "mine" | "all"
  const [needsConnection, setNeedsConnection] = useState(false);
  const [q, setQ] = useState("");
  const [guests, setGuests] = useState([]);
  const [me, setMe] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [myRequests, setMyRequests] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/teacher/me").then((r) => setMe(r.data)).catch(() => {});
  }, [user]);

  const loadRequests = () => {
    api.get("/teacher/my-requests").then((r) => setMyRequests(r.data || [])).catch(() => setMyRequests([]));
  };
  useEffect(() => { if (user) loadRequests(); }, [user]);

  useEffect(() => {
    if (!user) return;
    setDataLoading(true);
    api.get("/teacher/guests", { params: { filter, mine_only: scope === "mine" } })
      .then((r) => setGuests(r.data))
      .catch(() => setGuests([]))
      .finally(() => setDataLoading(false));
  }, [user, filter, scope]);

  const pendingRequestByGuest = useMemo(() => {
    const m = new Map();
    myRequests.filter((r) => r.status === "pending").forEach((r) => m.set(r.guest_id, r));
    return m;
  }, [myRequests]);

  const handleRequest = async (g) => {
    try {
      await api.post(`/teacher/guests/${g.id}/request`);
      toast.success(`Requested ${formatDisplayName(g)} — your coordinator will decide.`);
      loadRequests();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send request.");
    }
  };

  const handleCancelRequest = async (req) => {
    if (!req) return;
    if (!window.confirm("Withdraw this request?")) return;
    try {
      await api.delete(`/teacher/requests/${req.id}`);
      toast.success("Request withdrawn.");
      loadRequests();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't withdraw request.");
    }
  };

  const stats = useMemo(() => {
    const mine = guests.filter((g) => g.is_mine || g.is_mine_next_steps);
    const active = mine.filter((g) => g.stage !== "completed");
    const completed = mine.filter((g) => g.stage === "completed");
    return { mineTotal: mine.length, active: active.length, completed: completed.length, church: guests.length };
  }, [guests]);

  const needsConnectionCount = useMemo(
    () => guests.filter((g) => {
      const ic = g.progress?.initial_contact;
      return ic && (ic.done || 0) < (ic.total || 0);
    }).length,
    [guests],
  );

  const filtered = useMemo(() => {
    let list = guests;
    if (needsConnection) {
      list = list.filter((g) => {
        const ic = g.progress?.initial_contact;
        return ic && (ic.done || 0) < (ic.total || 0);
      });
    }
    if (!q.trim()) return list;
    const l = q.toLowerCase();
    return list.filter((g) =>
      `${g.first_name} ${g.last_name}`.toLowerCase().includes(l) ||
      (g.city || "").toLowerCase().includes(l) ||
      (g.phone || "").includes(l)
    );
  }, [guests, q, needsConnection]);

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-16" style={{ color: "var(--muted)" }}>Loading…</div>;
  if (!user) return <Navigate to="/teacher/login?next=/teacher" replace />;
  if (!user.is_teacher && !user.is_admin) return <Navigate to="/teacher/login" replace />;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10 lg:py-12" data-testid="teacher-dashboard">
      {/* Greeting + org */}
      <header className="mb-8">
        <div className="overline mb-2">Home Study Portal</div>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>
          {me?.teacher?.name ? <>Welcome, <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>{me.teacher.name.split(" ")[0]}</span></> : "My guests"}
        </h1>
        {me?.org?.name && (
          <p className="mt-2 text-sm" style={{ color: "var(--muted)" }} data-testid="teacher-org-name">{me.org.name}</p>
        )}
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-8">
        <StatCard label="Assigned to you" value={stats.mineTotal} color="var(--ember)" icon={Users} testid="teacher-stat-mine" />
        <StatCard label="Active" value={stats.active} color="var(--terracotta)" icon={Compass} testid="teacher-stat-active" />
        <StatCard label="Completed" value={stats.completed} color="var(--olive)" icon={Award} testid="teacher-stat-completed" />
        <StatCard label="Phase 1 pipeline" value={stats.church} color="var(--ink)" icon={Users} testid="teacher-stat-church" hint={scope === "all" ? "Currently viewing all" : "Switch to All to see"} />
      </div>

      {/* Past requests (approved/denied history) */}
      <PastRequestsPanel requests={myRequests} />

      {/* Toolbar */}
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[16rem] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted)" }} />
          <input
            placeholder="Search name, city, phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md"
            data-testid="teacher-search"
          />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--surface-hover)" }} data-testid="teacher-scope-tabs">
            {[["mine", "Mine"], ["all", "All in church"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setScope(k)}
                data-testid={`teacher-scope-${k}`}
                className="px-3 py-1 text-xs rounded-full transition-colors"
                style={{
                  background: scope === k ? "var(--ink)" : "transparent",
                  color: scope === k ? "#FFFFFF" : "var(--ink-2)",
                  fontWeight: scope === k ? 600 : 400,
                }}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--surface-hover)" }} data-testid="teacher-filter-tabs">
            {[["active", "Active"], ["completed", "Completed"], ["all", "All"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`teacher-filter-${k}`}
                className="px-3 py-1 text-xs rounded-full transition-colors"
                style={{
                  background: filter === k ? "var(--ember)" : "transparent",
                  color: filter === k ? "#FFFFFF" : "var(--ink-2)",
                  fontWeight: filter === k ? 600 : 400,
                }}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            onClick={() => setNeedsConnection((v) => !v)}
            data-testid="teacher-filter-needs-connection"
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded-full transition-colors"
            style={{
              background: needsConnection ? "var(--gold)" : "var(--surface-hover)",
              color: needsConnection ? "var(--bg-base)" : "var(--ink-2)",
              border: needsConnection ? "1px solid var(--gold)" : "1px solid var(--border-subtle)",
              fontWeight: needsConnection ? 600 : 400,
            }}
            title="Show only guests whose Guest Connection isn't complete yet"
          >
            <Sparkles size={11} /> Needs welcome
            {needsConnectionCount > 0 && (
              <span
                className="ml-0.5 text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                style={{
                  background: needsConnection ? "var(--bg-base)" : "var(--surface)",
                  color: needsConnection ? "var(--gold)" : "var(--muted)",
                }}
              >
                {needsConnectionCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {dataLoading ? (
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      ) : filtered.length === 0 ? (
        needsConnection ? (
          <EmptyState
            icon={Sparkles}
            title="Everyone's been welcomed"
            description="Every guest in this view has completed Guest Connection. Great work — turn the filter off to see the full list."
            action={
              <button onClick={() => setNeedsConnection(false)} className="btn-ghost" data-testid="teacher-empty-clear-needs-btn">
                Show all guests
              </button>
            }
            testid="teacher-empty-state"
          />
        ) : filter === "completed" ? (
          <EmptyState
            icon={CheckCircle2}
            title="No completed guests yet"
            description="When a guest finishes Graduation, they'll appear here — a snapshot of the fruit of your discipleship."
            testid="teacher-empty-state"
          />
        ) : scope === "mine" ? (
          <EmptyState
            icon={Users}
            title="No guests assigned to you yet"
            description="Your coordinator hasn't assigned you any guests. Switch to “All in church” to see who's currently being discipled around you."
            action={
              <button onClick={() => setScope("all")} className="btn-ghost" data-testid="teacher-empty-scope-btn">
                See everyone in your church
              </button>
            }
            testid="teacher-empty-state"
          />
        ) : (
          <EmptyState
            icon={Users}
            title="No Phase 1 guests yet"
            description="Once a coordinator adds guests to the Phase 1 pipeline, they'll show up here — even before they're assigned to a teacher."
            testid="teacher-empty-state"
          />
        )
      ) : (
        <div className="grid grid-cols-1 gap-2">
          {filtered.map((g) => <GuestCard key={g.id} g={g} pendingRequest={pendingRequestByGuest.get(g.id)} onRequest={handleRequest} onCancelRequest={handleCancelRequest} />)}
        </div>
      )}
    </div>
  );
}
